// "Auto-shrink to fit width" (legacy autoFitText, snapshot L5491-5506) — the v2
// port carried the checkbox/flag but no implementation, and the classic
// algorithm measured getScaledWidth(), which for a WRAPPING Textbox always
// equals the box width, so it could never shrink one. This version measures
// the natural single-line width of the text (widest explicit line) and scales
// the font so nothing wraps. Applied live (typing / resizing / toggling) and by
// the batch renderer after {{field}} substitution.
import { FabricText, type FabricObject } from 'fabric'

export const AUTOFIT_MIN_FONT = 4

type TextLike = FabricObject & {
  text?: string
  fontSize?: number
  fontFamily?: string
  fontWeight?: string | number
  fontStyle?: string
  charSpacing?: number
  width?: number
  autoFitWidth?: boolean
  _autoFitOrigSize?: number
  initDimensions?: () => void
  setCoords: () => void
}

export function isTextLike(o: FabricObject | undefined | null): o is TextLike {
  const t = (o?.type ?? '').toLowerCase()
  return t === 'textbox' || t === 'i-text' || t === 'text'
}

/** Natural (unwrapped) width of the widest line at the given font size. */
function naturalWidth(o: TextLike, fontSize: number): number {
  const lines = (o.text ?? '').split('\n')
  let max = 0
  for (const line of lines) {
    const probe = new FabricText(line || ' ', {
      fontFamily: o.fontFamily, fontSize, fontWeight: o.fontWeight, fontStyle: o.fontStyle as 'normal' | 'italic' | 'oblique' | undefined, charSpacing: o.charSpacing,
    })
    max = Math.max(max, probe.width)
  }
  return max
}

/**
 * Shrink `o.fontSize` (never below AUTOFIT_MIN_FONT, never above the size the
 * user chose) so the text fits its box on one line. Returns true if changed.
 * `_autoFitOrigSize` remembers the user's size so shorter text grows back.
 */
export function fitTextToWidth(o: FabricObject): boolean {
  if (!isTextLike(o) || !o.autoFitWidth) return false
  const orig = o._autoFitOrigSize ?? o.fontSize ?? 16
  o._autoFitOrigSize = orig
  const maxW = Math.max(1, (o.width ?? 0) - 1) // Textbox wraps at exactly `width`; leave 1px
  const natural = naturalWidth(o, orig)
  let next = orig
  if (natural > maxW) next = Math.max(AUTOFIT_MIN_FONT, Math.floor((orig * maxW / natural) * 2) / 2) // 0.5pt steps like legacy
  if (next === o.fontSize) return false
  o.set({ fontSize: next } as Partial<TextLike>)
  o.initDimensions?.()
  o.setCoords()
  return true
}

/** The user picked a new size in the panel: it becomes the auto-fit ceiling. */
export function setAutoFitBaseSize(o: FabricObject, fontSize: number): void {
  if (!isTextLike(o)) return
  o._autoFitOrigSize = fontSize
  o.set({ fontSize } as Partial<TextLike>)
  fitTextToWidth(o)
}

/** Apply to every auto-fit text object in a tree (groups recurse). */
export function fitAllTexts(objs: FabricObject[]): void {
  for (const o of objs) {
    fitTextToWidth(o)
    const kids = (o as FabricObject & { getObjects?: () => FabricObject[] }).getObjects
    if (typeof kids === 'function') fitAllTexts(kids.call(o))
  }
}
